// @flow

import { useMemo } from 'react';
import { useQuery } from 'react-query';
import isEmpty from 'lodash/isEmpty';

import ComplianceApprovalService from '../../services/complianceApprovalService';

// The per-stage open-cert counts for one property. The organization and
// property ids are part of the query key, so selecting a different property
// fetches that property's counts (and react-query caches each separately). The
// endpoint is tenant-scoped under `/{organizationId}/{propertyId}/`; the query
// is disabled until both ids are known, and the open-certs filter lives
// entirely in the backend query.
//
// useErrorBoundary is deliberately false: a failure surfaces as the screen's
// own inline error state (via AsyncBox), not a blanked page.
const useComplianceApprovalCountsQuery = ({
  organizationId,
  propertyId,
  options = {},
}: {
  organizationId: ?string,
  propertyId: ?string,
  options?: Object,
}) => {
  const complianceApprovalService = useMemo(
    () => new ComplianceApprovalService(),
    [],
  );

  return useQuery(
    ['compliance-approval-counts', organizationId, propertyId],
    () => {
      // `enabled` below keeps this from running without an org + property, but
      // the guard also narrows both from ?string to string for getCounts.
      if (!organizationId || !propertyId) {
        throw new Error('organizationId and propertyId are required');
      }
      return complianceApprovalService.getCounts(organizationId, propertyId);
    },
    {
      refetchOnWindowFocus: false,
      useErrorBoundary: false,
      enabled: !isEmpty(organizationId ?? '') && !isEmpty(propertyId ?? ''),
      ...options,
    },
  );
};

export default useComplianceApprovalCountsQuery;
